import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Eye, 
  Brain, 
  Mic, 
  Zap, 
  Shield, 
  Command, 
  CheckCircle, 
  ArrowRight,
  Sparkles,
  HelpCircle,
  Settings,
  X,
  Calendar,
  Plus,
  Rocket,
  Users
} from 'lucide-react';
import EmailSignupForm from './EmailSignupForm';
import Header from './Header';
import Footer from './Footer';

function HomePage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const handleEmailSubmit = (email) => {
    setIsSubmitted(true);
    // The actual submission logic is handled in EmailSignupForm
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnalysis(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: <Eye className="w-8 h-8 text-clara-blue-600" />,
      title: "Vision Frame",
      description: "Transparent, draggable window for contextual screen analysis with AI-powered insights"
    },
    {
      icon: <Brain className="w-8 h-8 text-clara-blue-600" />,
      title: "Multi-AI Integration",
      description: "OpenAI, Gemini, Grok, and local Ollama support with intelligent fallback"
    },
    {
      icon: <Mic className="w-8 h-8 text-clara-blue-600" />,
      title: "Advanced Audio",
      description: "Real-time transcription with microphone + system audio for conversation insights"
    },
    {
      icon: <Zap className="w-8 h-8 text-clara-blue-600" />,
      title: "Smart Automation",
      description: "AppleScript/JavaScript automation for common tasks and workflows"
    },
    {
      icon: <Shield className="w-8 h-8 text-clara-blue-600" />,
      title: "Privacy-First",
      description: "Your data stays on your Mac. Local processing with optional cloud AI - you control what gets shared"
    },
    {
      icon: <Command className="w-8 h-8 text-clara-blue-600" />,
      title: "Global Shortcuts",
      description: "⌘A (analysis), ⌘Q (question), ⌘L (listen questions) for instant access"
    }
  ];

  const stats = [
    { icon: <Sparkles className="w-6 h-6" />, value: "4 AI Providers", label: "Supported" },
    { icon: <Shield className="w-6 h-6" />, value: "Privacy First", label: "Local Processing Available" },
    { icon: <Zap className="w-6 h-6" />, value: "Real-Time", label: "AI Assistance" },
    { icon: <Eye className="w-6 h-6" />, value: "Vision Frame", label: "Screen Analysis" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>ClaraAI — Vision-Driven macOS Productivity | Join the Waitlist</title>
        <meta name="description" content="Transparent Vision Frame for contextual screen analysis, multi‑AI integration, and real‑time automation. Join the ClaraAI waitlist." />
        <link rel="canonical" href="https://claraai.app/" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-20">
        {/* Dynamic Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"></div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`,
            backgroundSize: '100% 100%'
          }}></div>
        </div>
        
        {/* Floating UI Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-24 h-24 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 text-center lg:text-left"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm">
                  <Sparkles className="w-4 h-4 mr-2 text-blue-300" />
                  Next-Gen Productivity (macOS First, Windows Coming Soon)
                </div>
              </motion.div>
              
              {/* Main Headline */}
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
              >
                Meet <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">ClaraAI</span>
              </motion.h1>
              
              {/* Subtitle */}
              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl leading-relaxed"
              >
                Stop context-switching between apps. ClaraAI's <span className="text-blue-300 font-semibold">breakthrough, transparent Vision Frame technology</span> instantly understands what you're working on and delivers AI-powered assistance exactly when you need it. <span className="text-green-300 font-semibold">Your productivity just got a 10x upgrade.</span>
              </motion.p>
              
              {/* CTA Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mb-8"
              >
                {!isSubmitted ? (
                  <EmailSignupForm onSubmit={handleEmailSubmit} />
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="inline-flex items-center px-6 py-4 bg-green-500/20 backdrop-blur-md border border-green-400/30 rounded-xl text-green-300"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="font-semibold">You're on the waitlist! Check your email for confirmation.</span>
                  </motion.div>
                )}
              </motion.div>
              
              {/* Feature Pills */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap justify-center lg:justify-start gap-3 mb-4"
              >
                <div className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm">
                  ✨ AI-Powered
                </div>
                <div className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm">
                  🔒 Privacy-First
                </div>
                <div className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm">
                  ⚡ Real-Time
                </div>
              </motion.div>
              
              {/* Platform Availability */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="flex items-center justify-center lg:justify-start space-x-4 text-sm text-gray-300"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>macOS 13.0+ (Launch)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span>Windows (Coming Soon)</span>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Right Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex-1 flex justify-center lg:justify-end"
            >
              <div className="relative w-full max-w-2xl">
                {/* Vision Frame Bar */}
                <div className="rounded-full overflow-hidden border border-blue-400/50 shadow-2xl backdrop-blur-md bg-gray-900/20 relative">
                  
                  {/* Main Bar */}
                  <div className="h-16 px-6 flex items-center justify-between relative z-10">
                    {/* Left Section - ClaraAI Logo */}
                    <div className="flex items-center -ml-4">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center overflow-hidden shadow-sm">
                        <img 
                          src="/assets/claraailogo4.png" 
                          alt="ClaraAI" 
                          className="w-12 h-12 object-cover rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Center Section - Action Buttons */}
                    <div className="flex items-center space-x-3 ml-4">
                      <button className="flex items-center space-x-2 px-4 py-2 bg-gray-700/70 border border-gray-600/50 rounded-lg text-white text-sm hover:bg-gray-600/70 transition-all duration-200">
                        <span>Listen</span>
                        <Mic className="w-4 h-4" />
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-gray-700/70 border border-gray-600/50 rounded-lg text-white text-sm hover:bg-gray-600/70 transition-all duration-200 whitespace-nowrap">
                        <span>Ask ClaraAI</span>
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500/40 border border-blue-400/30 rounded-lg text-white text-sm hover:bg-blue-500/50 transition-all duration-200 shadow-md">
                        <span>Answer</span>
                        <Brain className="w-4 h-4" />
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-purple-500/30 border border-purple-400/20 rounded-lg text-white text-sm hover:bg-purple-500/40 transition-all duration-200 shadow-md">
                        <span>Agent</span>
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Right Section - Utility Icons */}
                    <div className="flex items-center space-x-4">
                      <div className="relative flex items-center justify-center w-6 h-6 hover:scale-110 transition-transform duration-200">
                        <Calendar className="w-5 h-5 text-orange-400" />
                        <Plus className="w-3 h-3 text-white absolute -top-0.5 -right-0.5" />
                      </div>
                      <Settings className="w-5 h-5 text-white cursor-pointer hover:text-gray-300 transition-colors hover:scale-110" />
                      <X className="w-5 h-5 text-white cursor-pointer hover:text-gray-300 transition-colors hover:scale-110" />
                    </div>
                  </div>
                </div>

                {/* Description Section Below Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mt-6"
                >
                  {!showAnalysis ? (
                    /* Thinking Animation */
                    <div className="bg-gray-900/30 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white shadow-xl">
                      <div className="text-center">
                        <div className="relative inline-block">
                          <span className="text-gray-400 text-lg font-medium">Thinking</span>
                          <div className="absolute inset-0 overflow-hidden">
                            <motion.span
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                              initial={{ x: '-100%' }}
                              animate={{ x: '100%' }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: 'easeInOut'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* ClaraAI Analysis */
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="bg-gray-900/30 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-white shadow-xl"
                    >
                      <h3 className="text-center text-white font-semibold text-lg mb-4">
                        ClaraAI's Analysis:
                      </h3>
                      <p className="text-gray-100 text-base leading-relaxed">
                        The website you are looking at is for ClaraAI, an AI-powered productivity tool designed to help users manage tasks and improve efficiency by understanding the context of their work. It features a technology called Vision Frame that provides assistance in real-time, enhancing productivity. Users can join a waitlist for early access and updates.
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-clara-blue-100 rounded-xl text-clara-blue-600 mb-4">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold text-clara-gray-900 mb-1">{stat.value}</div>
                <div className="text-clara-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Compatibility Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-clara-gray-900 mb-6">
              Works with your favorite apps
            </h3>
            <p className="text-xl text-clara-gray-600 max-w-3xl mx-auto leading-relaxed">
              ClaraAI seamlessly integrates with the communication platforms you already use, 
              making it easy to enhance your workflow without disruption.
            </p>
          </div>
          
          <div className="relative">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-clara-blue-50/50 to-purple-50/50 rounded-3xl blur-3xl"></div>
            
            {/* Logo grid */}
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-12 border border-gray-100 shadow-xl">
              <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-16">
                {/* Discord */}
                <div className="w-28 h-28 bg-[#5865F2] rounded-2xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform duration-300">
                  <svg className="w-14 h-14 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                </div>
                
                {/* Slack */}
                <div className="w-28 h-28 bg-white rounded-2xl flex items-center justify-center shadow-xl p-3 transform hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/assets/slack-logo-png_seeklogo-496177.png" 
                    alt="Slack" 
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Google Meet */}
                <div className="w-28 h-28 bg-white rounded-2xl flex items-center justify-center shadow-xl p-3 transform hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/assets/google-meet-logo-3.png" 
                    alt="Google Meet" 
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Zoom */}
                <div className="w-28 h-28 bg-black rounded-2xl flex items-center justify-center shadow-xl p-3 transform hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/assets/zoom-logo-png_seeklogo-479743.png" 
                    alt="Zoom" 
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* FaceTime */}
                <div className="w-28 h-28 rounded-3xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/assets/FaceTime-Logo-2018-present-700x394.png" 
                    alt="FaceTime" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-clara-gray-900 mb-6">
              Powerful Features for <span className="gradient-text">Modern Productivity</span>
            </h2>
            <p className="text-xl text-clara-gray-600 max-w-3xl mx-auto">
              ClaraAI combines cutting-edge AI technology with intuitive macOS design to create 
              the most powerful productivity experience ever built.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
              >
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-clara-gray-900 mb-3">{feature.title}</h3>
                <p className="text-clara-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Urgency */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div>
            {/* Urgency Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-full text-red-300 font-bold text-sm mb-8">
              <Rocket className="w-4 h-4 mr-2" />
              ⚠️ LIMITED SPOTS AVAILABLE
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Don't Miss Out on the
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Productivity Revolution
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Be among the first to experience the future of productivity with exclusive early access to ClaraAI.  
              <span className="text-yellow-300 font-semibold"> Limited spots available!</span>
            </p>
            
            {/* Dual CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button
                onClick={() => document.getElementById('email-signup').scrollIntoView({ behavior: 'smooth' })}
                className="group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-6 px-12 rounded-2xl transition-all duration-300 shadow-2xl text-lg min-w-[280px] relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center">
                  🚀 Secure My Access
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              
              <div className="text-center sm:text-left">
                <div className="text-white font-semibold text-lg mb-1">
                  ✅ FREE Early Access
                </div>
                <div className="text-gray-300 text-sm">
                  No credit card • Exclusive features
                </div>
              </div>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-300">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">Privacy Protected</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium">Instant Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium">Join the Waitlist</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage; 